'use client';

import React, {
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import { Drawer, Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useConnection } from '@sezenta/connection';
import { useEvents } from '../../common/EventContext';
import CTag from '../../components/ui/CTag';

export type MasterDataViewModalInstance = {
  view: (data: any) => void;
};

type Props = { onEdit?: (data: any) => void };

type FieldProps = {
  label: string;
  value: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-gray-500 text-sm">{label}</span>
    <span className="font-medium">{value || 'N/A'}</span>
  </div>
);

type FieldGroupProps = {
  fields: { label: string; value: React.ReactNode }[];
};

const FieldGroup: React.FC<FieldGroupProps> = ({ fields }) => (
  <div className="grid grid-cols-2 gap-4">
    {fields.map((field, index) => (
      <Field key={index} label={field.label} value={field.value} />
    ))}
  </div>
);

const MasterDataViewModal: ForwardRefRenderFunction<
  MasterDataViewModalInstance,
  Props
> = (props, ref) => {
  const [data, setData] = useState<any>();
  const { t } = useTranslation();
  const connection = useConnection();
  const events = useEvents();
  const [deleting, setDeleting] = useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();

  useImperativeHandle(
    ref,
    () => ({
      view: (d: any) => {
        setData(d);
      },
    }),
    [],
  );

  return (
    <>
      <Drawer
        width={480}
        destroyOnClose
        title={
          <div className="flex items-center justify-between">
            <span className="text-lg font-medium">Master Data Details</span>
          </div>
        }
        open={!!data}
        onClose={() => setData(undefined)}
        footer={null}
      >
        {data && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-2">{data.name}</h3>
              <CTag
                prefix="dot"
                color={data.status === 'Active' ? 'green' : 'orange'}
              >
                {data.status === 'Active' ? 'ACTIVE' : 'INACTIVE'}
              </CTag>
            </div>

            <div className="space-y-4">
              <Field label="Type" value={data.type} />
              <Field label="Name" value={data.name} />
              <Field label="Status" value={data.status} />

              {/* Show weight and states only for CollectionType */}
              {data.type === 'CollectionType' && (
                <>
                  <Field label="Weight" value={data.weight} />
                  <Field
                    label="States"
                    value={
                      Array.isArray(data.state)
                        ? data.state.join(', ')
                        : data.state
                    }
                  />
                  <Field
                    label="Visible to Driver"
                    value={
                      <CTag
                        prefix="dot"
                        color={data.visibleToDriver !== false ? 'green' : 'red'}
                      >
                        {data.visibleToDriver !== false ? 'YES' : 'NO'}
                      </CTag>
                    }
                  />
                  <Field
                    label="Default"
                    value={
                      <CTag
                        prefix="dot"
                        color={data.default === true ? 'green' : 'red'}
                      >
                        {data.default === true ? 'YES' : 'NO'}
                      </CTag>
                    }
                  />
                </>
              )}

              {data.extraInfo && (
                <Field label="Extra Info" value={data.extraInfo} />
              )}
            </div>
          </div>
        )}
      </Drawer>
      {contextHolder}
    </>
  );
};

export const useMasterDataViewModal = () =>
  useRef<MasterDataViewModalInstance>(null);

export default React.forwardRef<MasterDataViewModalInstance, Props>(
  MasterDataViewModal,
);
